import { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { trackModuleImpression } from '../../../libraries/_modules/tracking/helpers';

/**
 * Fires a single module impression event once the module is at least
 * `threshold` visible on screen.
 *
 * @param {object} trackingData - Payload passed to trackModuleImpression.
 * @param {object} [config]
 * @param {boolean} [config.enabled=true] - Gate tracking until data is ready.
 * @param {number} [config.threshold=0.5] - Visibility ratio required to fire.
 * @returns {(node: Element|null) => void} ref - Attach to the module container.
 */
export default function useModuleImpression(trackingData, config = {}) {
  const { enabled = true, threshold = 0.5 } = config;

  const tracked = useRef(false);
  const { ref, inView } = useInView({
    threshold,
    triggerOnce: true,
  });

  // Keep latest tracking data without re-running the effect on each render.
  const trackingDataRef = useRef(trackingData);
  trackingDataRef.current = trackingData;

  useEffect(() => {
    if (enabled && inView && !tracked.current) {
      tracked.current = true;
      trackModuleImpression(trackingDataRef.current);
    }
  }, [enabled, inView]);

  return ref;
}
